import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link as MuiLink,
  LinearProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

interface PasswordStrengthIndicator {
  score: number; // 0-4
  message: string;
  color: 'error' | 'warning' | 'info' | 'success';
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrengthIndicator>({
    score: 0,
    message: '',
    color: 'error',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const email = searchParams.get('email');
  const code = searchParams.get('code');

  useEffect(() => {
    // Validate that both email and code are present
    if (!email || !code) {
      setInvalidLink(true);
    }
  }, [email, code]);

  const calculatePasswordStrength = (password: string): PasswordStrengthIndicator => {
    if (!password) {
      return { score: 0, message: '', color: 'error' };
    }

    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;

    const strengthLevels: PasswordStrengthIndicator[] = [
      { score: 0, message: 'Very Weak', color: 'error' },
      { score: 1, message: 'Weak', color: 'error' },
      { score: 2, message: 'Fair', color: 'warning' },
      { score: 3, message: 'Good', color: 'info' },
      { score: 4, message: 'Strong', color: 'success' },
    ];

    return strengthLevels[Math.min(score, 4)];
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await axios.post('http://localhost:5222/api/auth/reset-password', {
        email,
        code,
        newPassword,
        confirmPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset failed:', err);
      const errorMessage = err.response?.data?.message;
      if (errorMessage?.includes('expired') || errorMessage?.includes('Invalid')) {
        setError('This password reset link has expired or is invalid. Please request a new one.');
        setInvalidLink(true);
      } else {
        setError(
          errorMessage ||
            'Failed to reset password. Please check your input and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (invalidLink && !success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom align="center">
                Invalid Reset Link
              </Typography>

              <Alert severity="error" sx={{ mb: 2 }}>
                The password reset link is invalid or has expired. Password reset links are only
                valid for 24 hours.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/forgot-password')}
                >
                  Request New Reset Link
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom align="center">
                Password Reset Successful
              </Typography>

              <Alert severity="success" sx={{ mb: 2 }}>
                Your password has been reset successfully. You can now login with your new
                password.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Create New Password
            </Typography>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Enter a strong password for your account
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                  disabled={loading}
                  helperText="Minimum 6 characters. Use uppercase, lowercase, and numbers for a stronger password."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {newPassword && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Password Strength</Typography>
                      <Typography variant="caption" color={passwordStrength.color}>
                        {passwordStrength.message}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(passwordStrength.score / 4) * 100}
                      color={passwordStrength.color}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                )}

                <TextField
                  fullWidth
                  required
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={loading}
                  error={confirmPassword !== '' && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword !== '' && newPassword !== confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || !newPassword || !confirmPassword}
                  fullWidth
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </Button>

                <Typography variant="body2" align="center">
                  <MuiLink component={Link} to="/login">
                    Back to Login
                  </MuiLink>
                </Typography>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ResetPassword;
